/* ════════════════════════════════════════════════════
   Run Monster Run — Shared front-end utilities
   Exposes a global window.RMR namespace used by sections.
   Keep this lean: section-specific JS lives in each section's
   <script> block, not here.
   ════════════════════════════════════════════════════ */
(function () {
  if (window.RMR) return;

  var scrollLockCount = 0;
  var savedScroll = 0;

  function lockScroll() {
    if (scrollLockCount === 0) {
      savedScroll = window.scrollY || document.documentElement.scrollTop || 0;
      var b = document.body;
      b.style.position = 'fixed';
      b.style.top = '-' + savedScroll + 'px';
      b.style.width = '100%';
    }
    scrollLockCount++;
  }

  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      var b = document.body;
      b.style.position = '';
      b.style.top = '';
      b.style.width = '';
      window.scrollTo(0, savedScroll);
    }
  }

  function onEsc(handler) {
    function listener(e) {
      if (e.key === 'Escape') handler(e);
    }
    document.addEventListener('keydown', listener);
    return function off() {
      document.removeEventListener('keydown', listener);
    };
  }

  function observe(el, callback, options) {
    if (!('IntersectionObserver' in window)) {
      callback({ isIntersecting: true, target: el });
      return function noop() {};
    }
    var opts = Object.assign({ threshold: 0.15, rootMargin: '0px 0px -50px 0px' }, options || {});
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) callback(entry);
      });
    }, opts);
    io.observe(el);
    return function disconnect() {
      io.disconnect();
    };
  }

  function animateNumber(el, from, to, duration) {
    var start = performance.now();
    var dur = duration || 1200;
    function ease(t) { return 1 - Math.pow(1 - t, 3); }
    function step(now) {
      var t = Math.min(1, (now - start) / dur);
      var v = Math.round(from + (to - from) * ease(t));
      el.textContent = v.toLocaleString();
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent('rmr:' + name, { detail: detail || {} }));
  }

  function on(name, handler) {
    document.addEventListener('rmr:' + name, handler);
    return function off() {
      document.removeEventListener('rmr:' + name, handler);
    };
  }

  /* Public API */
  window.RMR = {
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    onEsc: onEsc,
    observe: observe,
    animateNumber: animateNumber,
    emit: emit,
    on: on,
  };

  /* Reduced-motion preference flag for sections to read */
  window.RMR.prefersReducedMotion = (
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) || false;
})();

/* ════════════════════════════════════════════════════
   PDP enhancements — controlling Kalles' sticky ATC
   and hiding empty related/recently sections.
   Independent IIFE — safe to remove without touching RMR core.
   ════════════════════════════════════════════════════ */
(function () {
  'use strict';

  function initStickyATC() {
    var sticky = document.querySelector('.hdt-sticky-btn-atc');
    var mainATC = document.querySelector('.hdt-product-form__submit, button[name="add"]');
    if (!sticky || !mainATC) return;

    sticky.classList.add('rmr-sticky-atc');

    /* Show the sticky bar ONLY after the user has scrolled past the main
       ATC button (i.e. its entire box is now above the viewport top).
       Previously this used `!isInViewport` which also fired at page top
       when the ATC was still below the fold — making the sticky appear
       before the user had a chance to see the real button. */
    var update = function () {
      var r = mainATC.getBoundingClientRect();
      var scrolledPast = r.bottom < 0;
      if (scrolledPast) {
        sticky.classList.add('rmr-sticky-show');
      } else {
        sticky.classList.remove('rmr-sticky-show');
      }
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  function hideEmptyRecommendationSection(parent, productContainerSelector) {
    if (!parent) return;
    var inner = parent.querySelector(productContainerSelector);
    if (!inner) {
      parent.classList.add('rmr-section-empty');
      return;
    }

    var check = function () {
      var hasProducts = inner.querySelector(
        '.hdt-card-product, .hdt-card-product__wrapper, .card-product, [class*="card-product"]'
      );
      if (hasProducts) parent.classList.remove('rmr-section-empty');
      else parent.classList.add('rmr-section-empty');
    };

    check();
    setTimeout(check, 1500);
    setTimeout(check, 3000);
    try {
      var mo = new MutationObserver(check);
      mo.observe(inner, { childList: true, subtree: true });
    } catch (e) {}
  }

  function initEmptyRelatedRecently() {
    document
      .querySelectorAll('[class*="__related-products"]')
      .forEach(function (p) {
        hideEmptyRecommendationSection(p, 'product-recommendations, .related-products');
      });
    document
      .querySelectorAll('[class*="__recently-products"]')
      .forEach(function (p) {
        hideEmptyRecommendationSection(p, 'product-recently, .recently-products');
      });
  }

  /* ─── Mega-menu top alignment ──
     The mega menu uses `position: fixed` so it can span the viewport
     width. Its `top` needs to track the live bottom edge of the header
     stack (ticker + header), which differs by scroll state and breakpoint.
     We feed the live value into a CSS variable read by the mega-menu CSS. */
  function initMegaMenuTop() {
    var header = document.querySelector('.rmr-header');
    if (!header) return;
    var root = document.documentElement;
    function update() {
      var bottom = header.getBoundingClientRect().bottom;
      if (bottom < 0) bottom = 0;
      root.style.setProperty('--rmr-megamenu-top', Math.round(bottom) + 'px');
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    /* Also update when the header is hovered — covers the case where the
       header changed height between updates (e.g. ticker close button). */
    header.addEventListener('mouseenter', update);
  }

  /* (Old `initPdpProgressBar` removed.) */
  function initPdpProgressBar() { /* intentionally empty */ }

  /* ─── PDP Supporters split: take the combined supporters panel that
     renders below the product section, extract its two halves, and
     relocate them:
       • Countdown half → ABOVE main-product (full-width strip)
       • Progress half  → INTO info column, just above the ATC
     The original card stays in DOM but is hidden via CSS once split. */
  function initPdpSupportersSplit() {
    var supp = document.querySelector('.rmr-supp');
    if (!supp || supp.dataset.rmrSuppSplit === '1') return;
    var infoList = document.querySelector('.hdt-product-info__list');
    var mainSection = document.querySelector('[id*="__main-product"], .hdt-secion-main-product');
    var countdown = supp.querySelector('.rmr-supp__countdown');
    var progress = supp.querySelector('.rmr-supp__progress');
    /* Either half can be hidden independently via the schema toggles, so only
       require the page anchor + at least one half to relocate. */
    if (!mainSection || (!countdown && !progress)) return;

    /* Wrap the countdown half in a full-width strip ABOVE main-product. */
    if (countdown) {
      var cdStrip = document.createElement('div');
      cdStrip.className = 'rmr-supp-cd-strip';
      /* Re-use the section's CSS variables by copying its style attribute. */
      cdStrip.setAttribute('style', supp.getAttribute('style') || '');
      /* Forward the section's countdown data-attrs so the timer keeps ticking. */
      if (supp.getAttribute('data-target')) cdStrip.setAttribute('data-target', supp.getAttribute('data-target'));
      if (supp.getAttribute('data-on-expire')) cdStrip.setAttribute('data-on-expire', supp.getAttribute('data-on-expire'));
      /* Inner container so content respects site gutter. */
      var cdInner = document.createElement('div');
      cdInner.className = 'rmr-container rmr-supp-cd-strip__inner';
      cdStrip.appendChild(cdInner);
      cdInner.appendChild(countdown);
      /* Position OUTSIDE main-product section's parent so it spans full width. */
      var mainContainer = mainSection.closest('[id^="shopify-section"]') || mainSection;
      mainContainer.parentElement.insertBefore(cdStrip, mainContainer);
    }

    /* Drop the progress half into the info column above the ATC. */
    if (progress && infoList) {
      var atcAnchor = infoList.querySelector('.hdt-product__buy-button');
      if (!atcAnchor) atcAnchor = infoList.querySelector('.hdt-product__variant-picker');
      if (atcAnchor) {
        progress.classList.add('rmr-supp__progress--inline');
        /* Carry the section's CSS variables onto the relocated card so the
           bar fill / accent colours survive the move out of .rmr-supp scope. */
        var suppStyle = supp.getAttribute('style') || '';
        if (suppStyle) {
          var existing = progress.getAttribute('style') || '';
          progress.setAttribute('style', existing + ';' + suppStyle);
        }
        atcAnchor.insertAdjacentElement('beforebegin', progress);
      }
    }

    /* Hide what's left of the original panel (the now-empty card + container). */
    supp.classList.add('rmr-supp--split');
    supp.dataset.rmrSuppSplit = '1';
  }

  /* ─── Inject "Shop all →" link into the You-may-also-like header.
     The related-products section is customizer-managed AND lazy-loaded
     async by Kalles, so the heading often isn't in the DOM yet when our
     DOMContentLoaded init fires. We try once now, then arm a
     MutationObserver to inject as soon as the heading appears. */
  function makeShopAll(extraClass) {
    var a = document.createElement('a');
    a.href = '/collections/shop-all';
    a.className = 'rmr-related-shop-all rmr-btn rmr-btn--secondary rmr-btn--sm' + (extraClass ? ' ' + extraClass : '');
    a.innerHTML = 'Shop all <span aria-hidden="true"> →</span>';
    return a;
  }
  function tryInjectShopAll() {
    var section = document.querySelector('[class*="__related-products"]');
    if (!section) return false;
    var ok = true;
    /* Desktop: "Shop all" beside the heading (top-right). */
    var heading = section.querySelector('.hdt-h-text-block, h2, .hdt-heading-block');
    if (heading) {
      var wrap = heading.closest('[id*="pr-heading"]') || heading.parentElement;
      if (wrap && !wrap.querySelector('.rmr-related-shop-all:not(.rmr-related-shop-all--bottom)')) {
        wrap.appendChild(makeShopAll());
        wrap.classList.add('rmr-related-heading-row');
      }
    } else { ok = false; }
    /* Mobile: a centered copy below the whole recommendations block (works
       whether Kalles renders it as a slider or a grid; CSS shows one per
       breakpoint). */
    var recsBlock = section.querySelector('product-recommendations')
      || section.querySelector('hdt-slider, .hdt-slider__products');
    if (recsBlock) {
      if (!section.querySelector('.rmr-related-shop-all--bottom')) {
        recsBlock.insertAdjacentElement('afterend', makeShopAll('rmr-related-shop-all--bottom'));
      }
    } else { ok = false; }
    return ok;
  }
  function initRelatedShopAll() {
    /* The related-products section can be:
       (a) server-rendered on page load (heading available immediately), or
       (b) lazy-fetched after intersection (heading appears later).
       We hit both with: (1) immediate try, (2) MutationObserver that
       NEVER disconnects (Kalles re-renders related sections on cart
       changes etc.), and (3) belt-and-braces interval polling for 60s. */
    if (tryInjectShopAll()) { /* fall through — MO still useful for re-renders */ }
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function(){ tryInjectShopAll(); });
      mo.observe(document.body, { childList: true, subtree: true });
    }
    /* Polling fallback. */
    var tries = 0;
    var iv = setInterval(function(){
      tries++;
      if (tryInjectShopAll() || tries > 60) clearInterval(iv);
    }, 1000);
  }

  /* ─── PDP Complete-Set bundle widget: relocate into info column.
     Sits above the accordion when both exist. Both sections render
     standalone in markup; this JS just moves the DOM node into place. */
  function initPdpCompleteSet() {
    var bundle = document.querySelector('.rmr-complete[data-rmr-bundle-section]');
    if (!bundle || bundle.dataset.rmrBundleRelocated === '1') return;
    var infoList = document.querySelector('.hdt-product-info__list');
    if (!infoList) return;
    /* Anchor: the accordion (we want bundle right above it). If accordion
       hasn't been relocated yet, fall back to inserting after the trust
       badge / notify-stock / buy-button (any of which puts us in roughly
       the right region — the accordion init then inserts itself below). */
    var anchor = infoList.querySelector('.rmr-acc-section')
              || infoList.querySelector('.hdt-back-instock, .hdt-product__notify-stock')
              || infoList.querySelector('.hdt-product__buy-button');
    if (!anchor) return;
    bundle.classList.add('rmr-complete--inline');
    anchor.insertAdjacentElement('beforebegin', bundle);
    bundle.dataset.rmrBundleRelocated = '1';
  }

  /* ─── PDP Accordion: relocate into product info column + single-open ──
     The rmr-product-accordion section renders as a full-width section by
     default. On PDP, we want it inside the right-column under the ATC.
     Strategy: move the section's content into a new container injected
     after the buy-button block. Original section is hidden once moved.
     Single-open behaviour: when one <details> opens, sibling open ones close. */
  function initPdpAccordion() {
    var acc = document.querySelector('.rmr-acc-section');
    if (!acc || acc.dataset.rmrAccRelocated === '1') return;
    var infoList = document.querySelector('.hdt-product-info__list');
    if (!infoList) return; /* not on PDP */

    /* Anchor: insert AFTER the notify-when-back-in-stock block when present
       (so flow is: ATC → notify-me → accordion). Fall back to the buy-button
       block if notify-stock isn't rendered (e.g. in-stock products). */
    var anchor = infoList.querySelector('.hdt-back-instock, .hdt-product__notify-stock')
              || infoList.querySelector('.hdt-product__buy-button');
    if (!anchor) return;

    acc.classList.add('rmr-acc-section--inline');
    anchor.insertAdjacentElement('afterend', acc);
    acc.dataset.rmrAccRelocated = '1';

    /* Force-close every item on init (user wants none open by default). */
    acc.querySelectorAll('details.rmr-acc__item[open]').forEach(function (d) {
      d.removeAttribute('open');
    });

    /* Single-open behaviour. Listen on the accordion root for click on any
       summary; if the targeted item is about to open, close siblings first. */
    var accRoot = acc.querySelector('.rmr-acc');
    if (accRoot) {
      accRoot.addEventListener('click', function (e) {
        var summary = e.target.closest('summary.rmr-acc__summary');
        if (!summary) return;
        var item = summary.parentElement;
        if (!item || item.tagName !== 'DETAILS') return;
        /* If the item is currently closed, it's about to open — close others first. */
        if (!item.hasAttribute('open')) {
          accRoot.querySelectorAll('details.rmr-acc__item[open]').forEach(function (other) {
            if (other !== item) other.removeAttribute('open');
          });
        }
      });
    }
  }

  /* Init order matters for the info-column relocations:
       1. progress bar  → after description
       2. accordion     → after notify-stock
       3. complete-set  → before accordion (so it sits between
          notify-stock and accordion, which is the user-requested
          "above the accordion" position) */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initStickyATC();
      initEmptyRelatedRecently();
      initPdpProgressBar();
      initPdpSupportersSplit();
      initPdpAccordion();
      initPdpCompleteSet();
      initRelatedShopAll();
      initMegaMenuTop();
    });
  } else {
    initStickyATC();
    initEmptyRelatedRecently();
    initPdpProgressBar();
    initPdpSupportersSplit();
    initPdpAccordion();
    initPdpCompleteSet();
    initRelatedShopAll();
    initMegaMenuTop();
  }
})();

/* ════════════════════════════════════════════════════
   Kalles → RMR cart bridge.
   Kalles' AJAX ATC dispatches `cart:update` (bubbling to document) and
   opens its own <dialog id="CartDrawer">. We:
     1. Refresh the RMR drawer state on every cart change.
     2. Open the RMR drawer after an add.
     3. Force-close the Kalles dialog so only the RMR drawer is visible.
   The Kalles drawer is also hidden by CSS as a belt-and-braces safety net.
   ════════════════════════════════════════════════════ */
(function () {
  'use strict';

  function emit(name) {
    document.dispatchEvent(new CustomEvent(name));
  }

  function unlockKallesScroll() {
    /* Kalles freezes the page with <html scroll-lock> / html.no-scroll
       (base.css: `html.no-scroll, html[scroll-lock] { overflow: hidden }`)
       when its cart dialog opens. Because we force-close that dialog
       programmatically, Kalles never runs its own cleanup, so <html> stays
       overflow:hidden and the page can't scroll after the drawer closes.
       Clear it ourselves. (The RMR drawer keeps scroll locked while it is open
       via its own body position:fixed, so this is safe to call here.) */
    var H = document.documentElement;
    if (H.hasAttribute('scroll-lock')) H.removeAttribute('scroll-lock');
    H.classList.remove('no-scroll');
  }

  function closeKallesDrawer() {
    var dialog = document.getElementById('CartDrawer');
    if (dialog) {
      if (dialog.open) { try { dialog.close(); } catch (e) {} }
      if (dialog.hasAttribute('open')) dialog.removeAttribute('open');
    }
    unlockKallesScroll();
  }

  /* ATC success: refresh RMR drawer and open it. */
  function onCartAdd() {
    closeKallesDrawer();
    emit('rmr:cart-changed');
    emit('rmr:open-cart');
    /* Kalles re-opens its dialog after its own handlers run, so re-close
       across a few frames to win the race. */
    requestAnimationFrame(closeKallesDrawer);
    setTimeout(closeKallesDrawer, 30);
    setTimeout(closeKallesDrawer, 150);
    setTimeout(closeKallesDrawer, 400);
  }

  /* Cart mutation (qty change, remove via Kalles UI somehow): just resync. */
  function onCartUpdate() {
    closeKallesDrawer();
    emit('rmr:cart-changed');
    requestAnimationFrame(closeKallesDrawer);
    setTimeout(closeKallesDrawer, 30);
    setTimeout(closeKallesDrawer, 150);
  }

  document.addEventListener('cart:add', onCartAdd);
  document.addEventListener('cart:update', onCartAdd);
  document.addEventListener('cart:count', function () { emit('rmr:cart-changed'); });
  document.addEventListener('cart:reload', function () { emit('rmr:cart-changed'); });

  /* Kalles also opens its dialog via `dialogOpening`. If the dialog being
     opened is the cart, suppress it. */
  document.addEventListener('dialogOpening', function (e) {
    var t = e.target;
    if (!t) return;
    if (t.id === 'CartDrawer' || (t.closest && t.closest('#CartDrawer'))) {
      try { if (t.close) t.close(); } catch (err) {}
    }
  }, true);
})();

/* ════════════════════════════════════════════════════
   RMR pledge triggers → the pledge page.
   The pledge amount selector lives inline on /pages/pledge
   (sections/rmr-support.liquid). There is no popup anymore. Any
   [data-rmr-action="open-pledge"] button (header, funding bar, empty cart,
   hero, etc.) brings the shopper to the pledge page — or, if an inline pledge
   form is already on the current page, scrolls to it instead of navigating.
   (The old pledge-app fetch-bridge IIFE was removed too: the inline form
   dispatches `cart:add` itself, so no global window.fetch wrap is needed.)
   ════════════════════════════════════════════════════ */
(function () {
  'use strict';
  var PLEDGE_URL = '/pages/pledge';
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-rmr-action="open-pledge"]');
    if (!trigger) return;
    e.preventDefault();
    var inline = document.querySelector('[data-rmr-pledge-inline]');
    if (inline) {
      inline.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var first = inline.querySelector('[data-rmr-pledge-preset]');
      if (first) setTimeout(function () { try { first.focus({ preventScroll: true }); } catch (err) {} }, 350);
    } else {
      window.location.href = PLEDGE_URL;
    }
  });
})();

/* ════════════════════════════════════════════════════
   RMR product-card "bag" quick-action.
   Single-variant, available products add straight to cart (the cart bridge
   above then opens the RMR drawer). Variant or unavailable products fall back
   to the product page so the customer can choose options — matching the
   theme's own "Add to cart" / "Choose options" card behaviour. The eye icon
   is handled by the theme's native quick-view (btn-quick-view), not here.
   ════════════════════════════════════════════════════ */
(function () {
  'use strict';
  var cartAddUrl = (window.Shopify && Shopify.routes && Shopify.routes.root)
    ? Shopify.routes.root + 'cart/add.js'
    : '/cart/add.js';

  document.addEventListener('click', function (e) {
    var bag = e.target.closest && e.target.closest('[data-rmr-action="add-to-cart"]');
    if (!bag) return;
    e.preventDefault();
    e.stopPropagation();

    var single = bag.getAttribute('data-rmr-single') === '1';
    var available = bag.getAttribute('data-rmr-available') === '1';
    var vid = bag.getAttribute('data-variant-id');
    var url = bag.getAttribute('data-url');

    /* Variant products / unavailable → go to the product page. */
    if (!single || !available || !vid) {
      if (url) window.location.href = url;
      return;
    }

    /* Simple, in-stock product → AJAX add to cart, then open the drawer. */
    if (bag.hasAttribute('data-rmr-loading')) return;
    bag.setAttribute('data-rmr-loading', '');
    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:add'));
      })
      .catch(function () {
        if (url) window.location.href = url; /* fall back to PDP on error */
      })
      .then(function () {
        bag.removeAttribute('data-rmr-loading');
      });
  });
})();

/* ════════════════════════════════════════════════════
   RMR "Complete the set" bundle add — a single button adds every product
   shown in a featured section to the cart. Each id is added on its own so an
   out-of-stock one is skipped instead of failing the whole batch. The cart
   bridge above then opens the RMR drawer.
   ════════════════════════════════════════════════════ */
(function () {
  'use strict';
  var addUrl = (window.Shopify && Shopify.routes && Shopify.routes.root)
    ? Shopify.routes.root + 'cart/add.js' : '/cart/add.js';

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-rmr-action="add-bundle"]');
    if (!btn) return;
    e.preventDefault();
    var ids = (btn.getAttribute('data-rmr-add-ids') || '').split(',')
      .map(function (s) { return s.trim(); }).filter(Boolean);
    var fallback = btn.getAttribute('data-rmr-fallback-url');
    if (!ids.length) { if (fallback) window.location.href = fallback; return; }
    if (btn.hasAttribute('data-rmr-loading')) return;
    btn.setAttribute('data-rmr-loading', '');
    var label = btn.textContent, added = 0;
    btn.textContent = 'Adding…';
    ids.reduce(function (chain, id) {
      return chain.then(function () {
        return fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: id, quantity: 1 })
        }).then(function (r) { if (r.ok) added++; }).catch(function () {});
      });
    }, Promise.resolve()).then(function () {
      btn.textContent = label;
      btn.removeAttribute('data-rmr-loading');
      if (added > 0) document.dispatchEvent(new CustomEvent('cart:add'));
      else if (fallback) window.location.href = fallback;
    });
  });
})();

/* ════════════════════════════════════════════════════
   RMR AJAX product form — any product form with the class `rmr-ajax-cart`
   (e.g. the Board Game Feature section) adds via /cart/add.js instead of a
   full-page navigation to /cart. On success it dispatches `cart:add`, so the
   cart bridge above closes Kalles' dialog and opens the RMR drawer. If the
   AJAX add fails, it falls back to a native submit so the buyer isn't stuck.
   ════════════════════════════════════════════════════ */
(function () {
  'use strict';
  var addUrl = (window.Shopify && Shopify.routes && Shopify.routes.root)
    ? Shopify.routes.root + 'cart/add.js' : '/cart/add.js';

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form.rmr-ajax-cart')) return;
    e.preventDefault();
    if (form.hasAttribute('data-rmr-loading')) return;
    form.setAttribute('data-rmr-loading', '');

    var btn = form.querySelector('[type="submit"]');
    var btnHTML = btn ? btn.innerHTML : null;
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }

    function restore() {
      form.removeAttribute('data-rmr-loading');
      if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); if (btnHTML != null) btn.innerHTML = btnHTML; }
    }

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form)
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:add'));
        restore();
      })
      .catch(function () {
        /* AJAX failed — let the browser submit normally (lands on /cart) so
           the customer can still complete the purchase. */
        form.classList.remove('rmr-ajax-cart');
        restore();
        if (form.submit) form.submit();
      });
  }, false);
})();

/* ════════════════════════════════════════════════════
   RMR Quick View — self-contained product quick-view popup for the product
   cards (the theme's own quick-view isn't wired on this store). Fetches the
   product JSON, lets the customer pick a variant, and adds to cart (opening
   the RMR drawer via the cart bridge above). Modal is created once, lazily.
   ════════════════════════════════════════════════════ */
(function () {
  'use strict';
  var addUrl = (window.Shopify && Shopify.routes && Shopify.routes.root)
    ? Shopify.routes.root + 'cart/add.js' : '/cart/add.js';
  var cache = {}, modal, els = {}, state = { product: null, selected: null };

  function fmtMoney(cents) {
    /* Shared currency-aware formatter (active Markets currency). Falls back to
       Shopify.formatMoney with the BASE shop format only if it's unavailable. */
    if (window.rmrMoney) return window.rmrMoney(cents);
    var f = (window.theme && (window.theme.moneyFormat || window.theme.money_format))
      || (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents, f); } catch (e) {}
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function sizeUrl(src, w) {
    if (!src) return src;
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function build() {
    modal = document.createElement('div');
    modal.className = 'rmr-qv';
    modal.setAttribute('data-rmr-qv', '');
    modal.hidden = true;
    modal.innerHTML =
      '<div class="rmr-qv__overlay" data-rmr-qv-close></div>' +
      '<div class="rmr-qv__dialog" role="dialog" aria-modal="true" aria-label="Quick view">' +
        '<button type="button" class="rmr-qv__close" data-rmr-qv-close aria-label="Close quick view">&times;</button>' +
        '<div class="rmr-qv__grid">' +
          '<div class="rmr-qv__media">' +
            '<div class="rmr-qv__media-main">' +
              '<img alt="" data-rmr-qv-img>' +
              '<button type="button" class="rmr-qv__nav rmr-qv__nav--prev" data-rmr-qv-prev aria-label="Previous image" hidden><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg></button>' +
              '<button type="button" class="rmr-qv__nav rmr-qv__nav--next" data-rmr-qv-next aria-label="Next image" hidden><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg></button>' +
            '</div>' +
            '<div class="rmr-qv__thumbs" data-rmr-qv-thumbs></div>' +
          '</div>' +
          '<div class="rmr-qv__info">' +
            '<p class="rmr-qv__type" data-rmr-qv-type></p>' +
            '<h2 class="rmr-qv__title" data-rmr-qv-title></h2>' +
            '<div class="rmr-qv__price" data-rmr-qv-price></div>' +
            '<div class="rmr-qv__options" data-rmr-qv-options></div>' +
            '<button type="button" class="rmr-qv__add" data-rmr-qv-add></button>' +
            '<a class="rmr-qv__details" data-rmr-qv-details href="#">View full details &rarr;</a>' +
          '</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);
    ['img','type','title','price','options','add','details','thumbs','prev','next'].forEach(function (k) {
      els[k] = modal.querySelector('[data-rmr-qv-' + k + ']');
    });
    modal.addEventListener('click', function (e) { if (e.target.closest('[data-rmr-qv-close]')) close(); });
    els.add.addEventListener('click', addToCart);
    if (els.prev) els.prev.addEventListener('click', function () { showImage(state.imgIndex - 1); });
    if (els.next) els.next.addEventListener('click', function () { showImage(state.imgIndex + 1); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && modal && !modal.hidden) close(); });
  }

  function open() { modal.hidden = false; document.documentElement.style.overflow = 'hidden'; }
  function close() { if (modal) { modal.hidden = true; document.documentElement.style.overflow = ''; } }

  /* Swap the quick-view main image by index (wraps around). Shared by the
     thumbnails and the prev/next arrows. Images are preloaded in populate(),
     so this is instant. */
  function showImage(i) {
    var imgs = state.images || [];
    if (!imgs.length || !els.img) return;
    i = ((i % imgs.length) + imgs.length) % imgs.length;
    state.imgIndex = i;
    els.img.src = sizeUrl(imgs[i], 800);
    var tbs = els.thumbs.querySelectorAll('.rmr-qv__thumb');
    for (var k = 0; k < tbs.length; k++) tbs[k].setAttribute('aria-pressed', k === i ? 'true' : 'false');
  }

  function selectedOptions() {
    return Array.prototype.map.call(els.options.querySelectorAll('[data-rmr-qv-optgroup]'), function (g) {
      var sel = g.querySelector('[aria-pressed="true"]');
      return sel ? sel.getAttribute('data-value') : null;
    });
  }
  function syncVariant() {
    var groups = els.options.querySelectorAll('[data-rmr-qv-optgroup]');
    if (groups.length === 0) {
      // No option pickers (single-variant product) — use the first available variant.
      var vs = state.product.variants || [];
      state.selected = vs.filter(function (v) { return v.available; })[0] || vs[0] || null;
    } else {
      var opts = selectedOptions();
      state.selected = (state.product.variants || []).filter(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      })[0] || null;
    }
    var v = state.selected;
    if (v) {
      els.price.textContent = fmtMoney(v.price);
      if (v.featured_image && v.featured_image.src) els.img.src = sizeUrl(v.featured_image.src, 800);
      els.add.disabled = !v.available;
      els.add.textContent = v.available ? 'Add to cart' : 'Sold out';
    } else {
      els.add.disabled = true;
      els.add.textContent = 'Unavailable';
      var any = (state.product.variants || [])[0];
      els.price.textContent = any ? fmtMoney(any.price) : '';
    }
  }

  /* Apply the PDP's contextual filtering to the quick-view's own buttons:
       • each option shows only values with a real, available variant given the
         selections to its LEFT — Type is leftmost (always shown), Size filters
         by the chosen Type (Kid vs Adult run different size ranges)
       • if the selected value gets hidden, pick the first visible one
     This is why the Kid/Adult rule "wasn't applying" in the popup — the QV
     builds its own buttons, separate from the theme's variant picker. Fully
     data-driven (no hard-coded size lists) so it matches each product's range. */
  function qvUp(s) { return String(s == null ? '' : s).trim().toUpperCase(); }
  function qvApplyRules() {
    var p = state.product;
    if (!p || !els.options) { syncVariant(); return; }
    var groups = Array.prototype.slice.call(els.options.querySelectorAll('[data-rmr-qv-optgroup]'));
    if (!groups.length) { syncVariant(); return; }
    /* Currently-selected value per option index (button with aria-pressed). */
    var selected = {};
    groups.forEach(function (g) {
      var idx = +g.getAttribute('data-rmr-qv-optgroup');
      var sel = g.querySelector('[aria-pressed="true"]');
      if (sel) selected[idx] = qvUp(sel.getAttribute('data-value'));
    });
    /* Live if some available variant carries this value at its option index AND
       matches the current selection for every option to its left. */
    function live(idx, val) {
      var nv = qvUp(val);
      return (p.variants || []).some(function (v) {
        if (!v.available) return false;
        if (qvUp(v.options[idx]) !== nv) return false;
        for (var j = 0; j < idx; j++) {
          if (selected[j] != null && qvUp(v.options[j]) !== selected[j]) return false;
        }
        return true;
      });
    }
    groups.forEach(function (g) {
      var idx = +g.getAttribute('data-rmr-qv-optgroup');
      var btns = Array.prototype.slice.call(g.querySelectorAll('.rmr-qv__optval'));
      var canHide = btns.some(function (b) { return live(idx, b.getAttribute('data-value')); });
      var firstVisible = null, selHidden = false;
      btns.forEach(function (b) {
        var hide = canHide && !live(idx, b.getAttribute('data-value'));
        b.style.display = hide ? 'none' : '';
        if (!hide && !firstVisible) firstVisible = b;
        if (hide && b.getAttribute('aria-pressed') === 'true') selHidden = true;
      });
      if (selHidden && firstVisible) {
        btns.forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
        firstVisible.setAttribute('aria-pressed', 'true');
      }
    });
    syncVariant();
  }

  function populate(p, url) {
    state.product = p;
    els.type.textContent = p.type || '';
    els.title.textContent = p.title;
    els.details.href = url || ('/products/' + p.handle);
    var imgs = (p.images && p.images.length) ? p.images.slice(0, 8) : (p.featured_image ? [p.featured_image] : []);
    state.images = imgs;
    state.imgIndex = 0;
    els.img.src = imgs.length ? sizeUrl(imgs[0], 800) : '';
    els.img.alt = p.title;
    /* Preload every gallery image (800px) so switching is instant — no per-click
       fetch lag (same fix as the board game gallery). */
    imgs.forEach(function (src) { var pre = new Image(); pre.decoding = 'async'; pre.src = sizeUrl(src, 800); });
    // Gallery thumbnails — click swaps the main image (via showImage).
    els.thumbs.innerHTML = '';
    var multiImg = imgs.length > 1;
    if (multiImg) {
      imgs.forEach(function (src, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'rmr-qv__thumb';
        b.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
        var im = document.createElement('img');
        im.src = sizeUrl(src, 160); im.alt = ''; im.loading = 'lazy';
        b.appendChild(im);
        b.addEventListener('click', function () { showImage(i); });
        els.thumbs.appendChild(b);
      });
    }
    /* Prev/next arrows only when there's more than one image. */
    if (els.prev) els.prev.hidden = !multiImg;
    if (els.next) els.next.hidden = !multiImg;
    // Build option pickers (skip the default single-variant "Title" option)
    els.options.innerHTML = '';
    var hasOpts = p.options && !(p.options.length === 1 && (p.options[0] === 'Title' || p.options[0].name === 'Title'));
    if (hasOpts) {
      p.options.forEach(function (optName, oi) {
        var name = optName.name || optName, values = [];
        p.variants.forEach(function (v) { if (values.indexOf(v.options[oi]) < 0) values.push(v.options[oi]); });
        var g = document.createElement('div');
        g.className = 'rmr-qv__optgroup';
        g.setAttribute('data-rmr-qv-optgroup', oi);
        g.innerHTML = '<span class="rmr-qv__optname">' + name + '</span><div class="rmr-qv__optvals"></div>';
        var row = g.querySelector('.rmr-qv__optvals');
        values.forEach(function (val) {
          var b = document.createElement('button');
          b.type = 'button'; b.className = 'rmr-qv__optval';
          b.setAttribute('data-value', val); b.setAttribute('aria-pressed', 'false');
          b.textContent = val;
          b.addEventListener('click', function () {
            row.querySelectorAll('.rmr-qv__optval').forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
            b.setAttribute('aria-pressed', 'true'); qvApplyRules();
          });
          row.appendChild(b);
        });
        g.style.display = values.length > 1 ? '' : 'none';
        els.options.appendChild(g);
      });
    }
    // default to the first available variant
    var first = (p.variants || []).filter(function (v) { return v.available; })[0] || (p.variants || [])[0];
    if (first && hasOpts) {
      first.options.forEach(function (val, oi) {
        var g = els.options.querySelector('[data-rmr-qv-optgroup="' + oi + '"]');
        if (g) g.querySelectorAll('.rmr-qv__optval').forEach(function (x) {
          x.setAttribute('aria-pressed', x.getAttribute('data-value') === val ? 'true' : 'false');
        });
      });
    } else if (first) {
      state.selected = first;
    }
    qvApplyRules();
  }

  function addToCart() {
    if (!state.selected || !state.selected.available) return;
    els.add.disabled = true;
    var was = els.add.textContent;
    els.add.textContent = 'Adding…';
    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: state.selected.id, quantity: 1 })
    }).then(function (r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function () { close(); document.dispatchEvent(new CustomEvent('cart:add')); })
      .catch(function () { els.add.disabled = false; els.add.textContent = was; });
  }

  function load(handle, url) {
    if (!handle) { if (url) window.location.href = url; return; }
    if (!modal) build();
    open();
    els.title.textContent = 'Loading…';
    els.type.textContent = ''; els.price.textContent = ''; els.options.innerHTML = '';
    /* Clear the previous product's image + thumbs so it doesn't flash before
       the new product loads. */
    els.img.removeAttribute('src'); els.thumbs.innerHTML = '';
    els.add.disabled = true; els.add.textContent = 'Loading…';
    var get = cache[handle] || (cache[handle] = fetch('/products/' + handle + '.js').then(function (r) {
      if (!r.ok) throw new Error('product not found'); return r.json();
    }));
    get.then(function (p) { populate(p, url); }).catch(function () { close(); if (url) window.location.href = url; });
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-rmr-action="quick-view"]');
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();
    load(t.getAttribute('data-handle'), t.getAttribute('data-url'));
  });
})();
/* ----------------------------------------------------------------------------
   PDP buy-button: keep print-on-demand items reading "Add to cart" (never
   "Pre-order") AFTER a variant change. Kalles' JS recomputes the label from raw
   inventory (variant_state.pre_order = inventory_quantity<=0 && continue-selling),
   overriding the server-rendered label. RMR (Jun 2026): the team wants the ATC
   button to read "Add to cart" for ALL products incl. genuine pre-orders, so
   whenever the theme swaps any button to "Pre-order", restore "Add to cart".
   Driven by the data-rmr-* attributes set in blocks/_product-buy-button.liquid.
---------------------------------------------------------------------------- */
(function () {
  function fix(btn) {
    if (!btn) return;
    var atc = btn.getAttribute('data-rmr-atc-label');
    var pre = btn.getAttribute('data-rmr-preorder-label');
    if (!atc || !pre) return;
    var span = btn.querySelector('.hdt-btn-atc_text');
    if (!span) return;
    if (span.textContent.trim() === pre.trim()) { span.textContent = atc; }
  }
  function init(root) {
    (root || document).querySelectorAll('.hdt-product-form__submit[data-rmr-genuine-preorder]').forEach(function (btn) {
      if (btn.__rmrPreFix) return;
      btn.__rmrPreFix = true;
      fix(btn);
      var mo = new MutationObserver(function () { fix(btn); });
      mo.observe(btn, { childList: true, characterData: true, subtree: true });
    });
  }
  if (document.readyState !== 'loading') { init(); }
  else { document.addEventListener('DOMContentLoaded', function () { init(); }); }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();

/* ----------------------------------------------------------------------------
   PDP: Size options filtered by the Kid / Adult Type.
     • Kid   → show XS–XL (hide 2XL, 3XL, 4XL, 5XL)
     • Adult → show S–5XL (hide XS)
   Frontend rule — the valid sizes always show (independent of stock). Re-applies
   on Type change and after the theme re-renders the variant picker; if the
   selected size becomes invalid for the new type, the first valid size is picked.
   Only acts on products that have BOTH a Kid/Adult option and a size option.
---------------------------------------------------------------------------- */
(function () {
  var SIZE_SET = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
  var KID_HIDE = ['2XL', '3XL', '4XL', '5XL'];
  var ADULT_HIDE = ['XS'];
  var norm = function (s) { return (s || '').trim().toUpperCase(); };
  var isKid = function (v) { return norm(v).indexOf('KID') === 0; };
  var isAdult = function (v) { return norm(v).indexOf('ADULT') === 0; };

  function controlFor(radio, picker) {
    var id = radio.id, label = null;
    if (id) { var ls = picker.querySelectorAll('label[for]'); for (var i = 0; i < ls.length; i++) { if (ls[i].getAttribute('for') === id) { label = ls[i]; break; } } }
    if (!label && radio.nextElementSibling && radio.nextElementSibling.tagName === 'LABEL') label = radio.nextElementSibling;
    return radio.closest('.hdt-selector__item, .hdt-swatch__item') || label || radio;
  }

  function apply(picker) {
    var radios = picker.querySelectorAll('input[is-value], input[type="radio"][name]');
    if (!radios.length) return;
    var groups = {};
    radios.forEach(function (r) { (groups[r.name] = groups[r.name] || []).push(r); });
    var typeGroup = null, sizeGroup = null;
    Object.keys(groups).forEach(function (name) {
      var vals = groups[name].map(function (r) { return r.value; });
      if (vals.some(isKid) || vals.some(isAdult)) typeGroup = groups[name];
      if (vals.filter(function (v) { return SIZE_SET.indexOf(norm(v)) >= 0; }).length >= 2) sizeGroup = groups[name];
    });
    if (!typeGroup || !sizeGroup) return;
    var checkedType = typeGroup.filter(function (r) { return r.checked; })[0] || typeGroup[0];
    var tv = checkedType && checkedType.value;
    var hideList = isKid(tv) ? KID_HIDE : (isAdult(tv) ? ADULT_HIDE : []);
    var firstVisible = null, selectedHidden = false;
    sizeGroup.forEach(function (r) {
      var hide = hideList.indexOf(norm(r.value)) >= 0;
      var ctrl = controlFor(r, picker);
      var want = hide ? 'none' : '';
      if (ctrl && ctrl.style.display !== want) ctrl.style.display = want;
      if (r.disabled !== hide) r.disabled = hide;
      if (!hide && !firstVisible) firstVisible = r;
      if (hide && r.checked) selectedHidden = true;
    });
    if (selectedHidden && firstVisible && !firstVisible.checked) {
      firstVisible.checked = true;
      firstVisible.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function run() { document.querySelectorAll('[id^="variant-picker-"]').forEach(apply); }

  function init() {
    if (!document.querySelector('[id^="variant-picker-"]')) return; /* not a PDP with a picker */
    run();
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('input[is-value], input[type="radio"]')) {
        var p = t.closest('[id^="variant-picker-"]');
        if (p) apply(p);
      }
    });
    var raf = false;
    var mo = new MutationObserver(function () { if (raf) return; raf = true; requestAnimationFrame(function () { raf = false; run(); }); });
    mo.observe(document.body, { childList: true, subtree: true });
    setTimeout(run, 400);
  }
  if (document.readyState !== 'loading') { init(); }
  else { document.addEventListener('DOMContentLoaded', init); }
})();

/* ----------------------------------------------------------------------------
   PDP: Contextual variant-option filtering (data-driven).
     Apparel here is one product per design with a Type option (Kid/Youth vs
     Adult) and a Size option. Kid and Adult run DIFFERENT size ranges — Kid is
     XS–XL (some start at S), Adult is S–5XL (ranges vary: some stop at 4XL/3XL,
     one Adult even includes XS). Every variant is in stock; the ranges just
     don't overlap. With no filtering, picking "Kid" still shows 2XL–5XL and
     "Adult" still shows XS — sizes that have no variant for that Type — so the
     picker looks broken / "out of stock".

     Fix: for each option, show only the values that have a real, available
     variant given the selections of the options to its LEFT (Shopify's standard
     connected-option rule). Type is leftmost, so it is never filtered — both
     Kid and Adult always show. Size is filtered by the chosen Type, so the
     shopper only sees sizes that Type is actually made in. Fully data-driven —
     no hard-coded size lists, so it stays correct as ranges differ per product.

   Why data-driven (not Kalles' is-unavailable class): that class is only set
   when the merchant's `hide_sold_out` theme setting is ON; it's OFF here, so
   incompatible values get `is-disabled` instead and a class-based approach is a
   no-op. So we read the product's own /products/{handle}.js variant data.

   Why this is safe / non-destructive:
     • A value is hidden only when no available variant exists for it under the
       current left-option selection. Switch Type and the hidden sizes for the
       other Type reappear — nothing is permanently removed.
     • The leftmost option (Type) is never filtered, so the Kid/Adult toggle can
       never trap itself; switching Type that invalidates the current Size auto-
       selects the first valid size for the new Type.
     • Nothing in the catalog / Printful / Printify / inventory is changed —
       pure show/hide via a CSS class on the storefront.
     • Safety net: a group is never fully hidden; if the checked value gets
       hidden, the first live value is selected.
     • Re-applies on selection change + theme re-render (MutationObserver).
   Reversible: delete this IIFE + the .rmr-vh-hide rule in rmr-custom.css.
---------------------------------------------------------------------------- */
(function () {
  var cache = {};
  function norm(s) { return String(s == null ? '' : s).trim().toLowerCase(); }
  function getHandle() {
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }
  function controlFor(radio, picker) {
    var id = radio.id, label = null;
    if (id) { var ls = picker.querySelectorAll('label[for]'); for (var i = 0; i < ls.length; i++) { if (ls[i].getAttribute('for') === id) { label = ls[i]; break; } } }
    if (!label && radio.nextElementSibling && radio.nextElementSibling.tagName === 'LABEL') label = radio.nextElementSibling;
    return radio.closest('.hdt-selector__item, .hdt-swatch__item') || label || radio;
  }
  function apply(picker, prod) {
    var optNames = (prod.options || []).map(function (o) { return norm(o && o.name != null ? o.name : o); });
    var variants = (prod.variants || []).filter(function (v) { return v && v.available; });
    var radios = picker.querySelectorAll('input[is-value], input[type="radio"][name]');
    if (!radios.length) return;
    var groups = {};
    radios.forEach(function (r) { (groups[r.name] = groups[r.name] || []).push(r); });
    /* Currently-checked value per option index, so we can filter each option by
       the options to its LEFT (Type → Size). */
    var selected = [];
    Object.keys(groups).forEach(function (name) {
      var oi = optNames.indexOf(norm(name));
      if (oi < 0) return;
      var chk = groups[name].filter(function (r) { return r.checked; })[0];
      if (chk) selected[oi] = norm(chk.value);
    });
    /* A value is live if some available variant carries it at its own option
       index AND matches the current selection for every option to its left. */
    function live(oi, value) {
      var nv = norm(value);
      return variants.some(function (v) {
        var opts = v.options || [];
        if (norm(opts[oi]) !== nv) return false;
        for (var j = 0; j < oi; j++) {
          if (selected[j] != null && norm(opts[j]) !== selected[j]) return false;
        }
        return true;
      });
    }
    Object.keys(groups).forEach(function (name) {
      var arr = groups[name];
      var oi = optNames.indexOf(norm(name));
      if (oi < 0) return; /* can't map this group → leave it alone */
      var liveCount = arr.filter(function (r) { return live(oi, r.value); }).length;
      if (!liveCount) return; /* safety: never empty a group */
      var firstLive = null, selectedHidden = false;
      arr.forEach(function (r) {
        var dead = !live(oi, r.value);
        var ctrl = controlFor(r, picker);
        if (ctrl) ctrl.classList.toggle('rmr-vh-hide', dead);
        if (!dead) {
          /* Live value: keep it clickable + un-struck. Kalles greys an option
             when it's incompatible with the CURRENT combo (e.g. "Adult" while
             Size is XS, which only Kid has), which would trap the Kid/Adult
             toggle. This value is valid under the left-option selection, so let
             the shopper pick it — the size filter then swaps to a valid size.
             Add-to-cart is still gated by the real variant. */
          if (r.classList) r.classList.remove('is-disabled', 'is-unavailable');
          if (r.disabled) r.disabled = false;
          var w = r.closest('.hdt-selector__item, .hdt-swatch__item');
          if (w) w.classList.remove('is-disabled', 'is-unavailable');
          if (ctrl && ctrl.classList) ctrl.classList.remove('is-disabled', 'is-unavailable');
          if (!firstLive) firstLive = r;
        }
        if (dead && r.checked) selectedHidden = true;
      });
      if (selectedHidden && firstLive && !firstLive.checked) {
        firstLive.checked = true;
        firstLive.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
  function run(prod) { document.querySelectorAll('[id^="variant-picker-"]').forEach(function (p) { apply(p, prod); }); }
  function init() {
    if (!document.querySelector('[id^="variant-picker-"]')) return;
    var h = getHandle();
    if (!h) return;
    var get = cache[h] || (cache[h] = fetch('/products/' + h + '.js').then(function (r) { return r.json(); }));
    get.then(function (prod) {
      if (!prod || !prod.variants) return;
      run(prod);
      document.addEventListener('change', function (e) {
        var t = e.target;
        if (t && t.matches && t.matches('input[is-value], input[type="radio"]')) {
          var p = t.closest('[id^="variant-picker-"]');
          if (p) apply(p, prod);
        }
      });
      var raf = false;
      var mo = new MutationObserver(function () { if (raf) return; raf = true; requestAnimationFrame(function () { raf = false; run(prod); }); });
      mo.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { run(prod); }, 400);
    }).catch(function () {});
  }
  if (document.readyState !== 'loading') { init(); }
  else { document.addEventListener('DOMContentLoaded', init); }
})();
// build 1778890005 — quick-view gallery arrows + image preload
